/**
 * Comparator Page - Compare two Pokemon side by side
 */
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { GitCompare, Search, X, Swords } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { usePokemon } from '../hooks/usePokemon';
import { TypeBadgeGroup } from '../components/pokedex/TypeBadge';
import { StatsPanel } from '../components/pokedex/StatsBar';
import { TypeCalculator } from '../components/pokedex/TypeCalculator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { getMegasAsPokemonList } from '../data/megaEvolutions';
import { getRegionalFormsAsPokemonList } from '../data/regionalForms';
import { getAlternateFormsAsPokemonList } from '../data/alternateForms';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

// Pokemon selector component
function PokemonSelector({ value, onChange, label, allPokemon }) {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  const filteredPokemon = allPokemon.filter(p => {
    const searchLower = search.toLowerCase().trim();
    const searchNoHyphen = searchLower.replace(/-/g, ' ');
    
    const nameLower = p.name.toLowerCase();
    const nameNoHyphen = nameLower.replace(/-/g, ' ');
    
    const displayName = (p.isMega || p.isRegional || p.isAlternate) 
      ? (p.displayName || p.name || '').toLowerCase() 
      : p.name.toLowerCase();
    const displayNameNoHyphen = displayName.replace(/-/g, ' ');
    
    const baseName = (p.baseName || '').toLowerCase();
    
    const matchesSearch = 
      nameLower.includes(searchLower) || 
      nameNoHyphen.includes(searchNoHyphen) ||
      displayName.includes(searchLower) ||
      displayNameNoHyphen.includes(searchNoHyphen) ||
      baseName.includes(searchLower) ||
      String(p.baseId || p.id).includes(searchLower);
      
    return matchesSearch;
  }).slice(0, 100); // Increased slice to 100 to show more results in search
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          data-testid={`pokemon-selector-${label}`}
          className="w-full justify-between h-12"
        >
          {value ? (
            <span className="capitalize">
              #{String(value.id).padStart(3, '0')} - {value.name.replace(/-/g, ' ')}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('selectPokemon')}</span>
          )}
          <Search className="w-4 h-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput 
            placeholder={t('searchByName')} 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('noResults')}</CommandEmpty>
            <CommandGroup>
              {filteredPokemon.map(pokemon => {
                const displayId = pokemon.isMega ? pokemon.baseId : pokemon.id;
                const displayName = (pokemon.isMega || pokemon.isRegional)
                  ? (pokemon.displayName || pokemon.name)
                  : pokemon.name;
                const uniqueKey = pokemon.isMega ? `mega-${pokemon.id}` : 
                  pokemon.isRegional ? `regional-${pokemon.id}` : `poke-${pokemon.id}`;
                return (
                  <CommandItem
                    key={uniqueKey}
                    value={`${displayName}-${uniqueKey}`}
                    onSelect={() => {
                      onChange(pokemon);
                      setOpen(false);
                    }}
                  >
                    <span className="capitalize">
                      #{String(displayId).padStart(3, '0')} - {displayName.replace(/-/g, ' ')}
                      {pokemon.isMega && <span className="ml-1 text-yellow-500 text-xs">✦ MEGA</span>}
                      {pokemon.isRegional && <span className="ml-1 text-cyan-500 text-xs">● {pokemon.region?.toUpperCase()}</span>}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Pokemon comparison card
// Helper: Convert mega/regional stats object to PokeAPI format for StatsPanel
function convertStatsToApiFormat(statsObj) {
  if (!statsObj) return [];
  // If already in API format (array with stat.name), return as-is
  if (Array.isArray(statsObj)) return statsObj;
  return [
    { stat: { name: 'hp' }, base_stat: statsObj.hp || 0 },
    { stat: { name: 'attack' }, base_stat: statsObj.attack || 0 },
    { stat: { name: 'defense' }, base_stat: statsObj.defense || 0 },
    { stat: { name: 'special-attack' }, base_stat: statsObj.spAtk || 0 },
    { stat: { name: 'special-defense' }, base_stat: statsObj.spDef || 0 },
    { stat: { name: 'speed' }, base_stat: statsObj.speed || 0 },
  ];
}

function ComparisonCard({ pokemon: selectedPokemon, onClear }) {
  const { getPokemon, getSprites, getPokemonSpecies, getLocalizedName } = usePokemon();
  const { t } = useApp();
  const [pokemon, setPokemon] = useState(null);
  const [species, setSpecies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      if (!selectedPokemon) return;

      setLoading(true);

      if (selectedPokemon.isMega) {
        // For mega evolutions, use the data from the mega list directly
        if (mounted) {
          setPokemon(selectedPokemon);
          const speciesData = await getPokemonSpecies(selectedPokemon.baseId);
          setSpecies(speciesData);
          setLoading(false);
        }
      } else if (selectedPokemon.isRegional) {
        // For regional forms, use local data and fetch base species
        if (mounted) {
          setPokemon(selectedPokemon);
          const speciesData = await getPokemonSpecies(selectedPokemon.baseId);
          setSpecies(speciesData);
          setLoading(false);
        }
      } else {
        // For regular pokémon, fetch from API
        const [pokemonData, speciesData] = await Promise.all([
          getPokemon(selectedPokemon.id),
          getPokemonSpecies(selectedPokemon.id)
        ]);

        if (mounted) {
          setPokemon(pokemonData);
          setSpecies(speciesData);
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [selectedPokemon, getPokemon, getPokemonSpecies]);
  
  if (!selectedPokemon) {
    return (
      <div className="bg-card rounded-2xl border-2 border-dashed border-border p-8 h-full flex items-center justify-center">
        <p className="text-muted-foreground text-center">{t('selectPokemon')}</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
        <Skeleton className="h-40 w-40 mx-auto rounded-full" />
        <Skeleton className="h-8 w-32 mx-auto" />
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      </div>
    );
  }
  
  // Handle different data formats for regular vs mega/regional Pokemon
  const isMegaOrRegional = selectedPokemon.isMega || selectedPokemon.isRegional;
  
  // Sprite URL
  let spriteUrl;
  if (isMegaOrRegional) {
    spriteUrl = pokemon?.spriteUrl || pokemon?.sprite;
  } else {
    const sprites = getSprites(pokemon);
    spriteUrl = sprites?.default;
  }
  
  // Types: Megas/Regionals use string array ['fire', 'dragon'], API uses [{type: {name: 'fire'}}]
  let types;
  if (isMegaOrRegional) {
    types = pokemon?.types || [];
  } else {
    types = pokemon?.types?.map(t => t.type.name) || [];
  }
  
  // Stats: Convert mega/regional format to API format for StatsPanel
  const statsForPanel = isMegaOrRegional
    ? convertStatsToApiFormat(pokemon?.stats)
    : (pokemon?.stats || []);
  
  const totalStats = statsForPanel.reduce((sum, s) => sum + s.base_stat, 0);
  
  // Name
  const displayName = isMegaOrRegional
    ? (pokemon?.displayName || pokemon?.name)
    : (species ? getLocalizedName(species.names) : pokemon?.name);
  
  // Display ID (show base ID for megas/regionals)
  const displayId = isMegaOrRegional ? pokemon?.baseId : pokemon?.id;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-card rounded-2xl border border-border overflow-hidden"
    >
      {/* Header with image */}
      <div className={`relative p-6 ${
        selectedPokemon.isMega 
          ? 'bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30'
          : selectedPokemon.isRegional
            ? 'bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900/30 dark:to-teal-900/30'
            : 'bg-gradient-to-br from-violet-100 to-rose-100 dark:from-violet-900/30 dark:to-rose-900/30'
      }`}>
        <button
          onClick={onClear}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/80 dark:bg-black/50 hover:scale-110 transition-transform"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Mega/Regional badge */}
        {selectedPokemon.isMega && (
          <span className="absolute top-3 left-3 text-xs font-bold px-2 py-1 rounded-full bg-yellow-500 text-white">
            ✦ MEGA
          </span>
        )}
        {selectedPokemon.isRegional && (
          <span className="absolute top-3 left-3 text-xs font-bold px-2 py-1 rounded-full bg-cyan-500 text-white">
            ● {selectedPokemon.region?.toUpperCase()}
          </span>
        )}
        {selectedPokemon.isAlternate && (
          <span className="absolute top-3 left-3 text-xs font-bold px-2 py-1 rounded-full bg-indigo-500 text-white">
            ● FORM
          </span>
        )}
        
        <div className="flex flex-col items-center">
          {!imageLoaded && (
            <Skeleton className="w-32 h-32 rounded-full" />
          )}
          <img
            src={spriteUrl}
            alt={displayName}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${displayId}.png`;
            }}
            className={`w-32 h-32 object-contain drop-shadow-lg transition-opacity ${
              imageLoaded ? 'opacity-100' : 'opacity-0 absolute'
            }`}
          />
          <h3 className="mt-2 text-xl font-bold capitalize text-foreground">
            {displayName?.replace(/-/g, ' ')}
          </h3>
          <p className="text-sm text-muted-foreground">
            #{String(displayId).padStart(3, '0')}
          </p>
          <TypeBadgeGroup types={types} size="sm" className="mt-2" />
        </div>
      </div>
      
      {/* Stats */}
      <div className="p-4 space-y-4">
        <div className="text-center p-3 bg-gradient-to-r from-violet-500/10 to-rose-500/10 rounded-lg">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('total')}</p>
          <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{totalStats}</p>
        </div>
        
        <StatsPanel stats={statsForPanel} animated={false} />
        
        {/* Ability for megas */}
        {selectedPokemon.isMega && pokemon?.ability && (
          <div className="text-center p-2 bg-yellow-500/10 rounded-lg">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('abilities')}</p>
            <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400 capitalize">
              {pokemon.ability}
            </p>
          </div>
        )}
        
        <div className="pt-4 border-t border-border">
          <TypeCalculator types={types} />
        </div>
      </div>
    </motion.div>
  );
}

export default function Comparator() {
  const { t, language } = useApp();
  const { getPokemonList } = usePokemon();
  
  const [allPokemon, setAllPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pokemon1, setPokemon1] = useState(null);
  const [pokemon2, setPokemon2] = useState(null);
  
  // Fetch pokemon list with mega evolutions
  useEffect(() => {
    let mounted = true;

    const fetchList = async () => {
      const list = await getPokemonList();
      const megas = getMegasAsPokemonList();
      const regionals = getRegionalFormsAsPokemonList();
      const alternates = getAlternateFormsAsPokemonList();
      if (mounted) {
        // Combine regular pokémon with all mega evolutions, regional forms and alternates
        setAllPokemon([...list, ...megas, ...regionals, ...alternates]);
        setLoading(false);
      }
    };

    fetchList();
    return () => { mounted = false; };
  }, [getPokemonList]);
  
  return (
    <div className="min-h-screen" data-testid="comparator-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground flex items-center justify-center gap-3">
            <GitCompare className="w-8 h-8 text-violet-500" />
            {t('comparator')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {language === 'es'
              ? 'Compara las estadísticas de dos Pokémon'
              : 'Compare stats of two Pokémon'
            }
          </p>
        </motion.div>
        
        {/* Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <PokemonSelector
            value={pokemon1}
            onChange={setPokemon1}
            label="1"
            allPokemon={allPokemon}
          />
          <PokemonSelector
            value={pokemon2}
            onChange={setPokemon2}
            label="2"
            allPokemon={allPokemon}
          />
        </div>
        
        {/* Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
          <ComparisonCard 
            pokemon={pokemon1} 
            onClear={() => setPokemon1(null)} 
          />
          
          {/* VS indicator */}
          {pokemon1 && pokemon2 && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden lg:flex">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-600 to-rose-500 flex items-center justify-center shadow-lg"
              >
                <Swords className="w-8 h-8 text-white" />
              </motion.div>
            </div>
          )}
          
          <ComparisonCard 
            pokemon={pokemon2} 
            onClear={() => setPokemon2(null)} 
          />
        </div>
      </div>
    </div>
  );
}
